"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface ModalV2Props {
  open?: boolean;
  isOpen?: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}

const SIZE_MAP: Record<NonNullable<ModalV2Props["size"]>, string> = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
};

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function ModalV2({
  open,
  isOpen,
  onClose,
  title,
  children,
  footer,
  closeOnBackdrop = true,
  closeOnEscape = true,
  size = "md",
  className,
}: ModalV2Props) {
  const isModalOpen = open ?? isOpen ?? false;
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const previousFocusRef = React.useRef<HTMLElement | null>(null);
  const prevOverflowRef = React.useRef("");
  const uid = React.useId();
  const titleId = title ? `${uid}-title` : undefined;
  const contentId = `${uid}-content`;

  // Open: store previous focus, lock body, auto-focus
  React.useEffect(() => {
    if (!isModalOpen) return;

    previousFocusRef.current = document.activeElement as HTMLElement;
    prevOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus first focusable or dialog itself
    setTimeout(() => {
      if (!dialogRef.current) return;
      const first = dialogRef.current.querySelector<HTMLElement>(FOCUSABLE);
      if (first) {
        first.focus();
      } else {
        dialogRef.current.focus();
      }
    }, 50);

    return () => {
      document.body.style.overflow = prevOverflowRef.current;
      previousFocusRef.current?.focus();
    };
  }, [isModalOpen]);

  // Focus trap
  const handleFocusTrap = React.useCallback((e: KeyboardEvent) => {
    if (e.key !== "Tab" || !dialogRef.current) return;

    const nodes = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (nodes.length === 0) return;

    const first = nodes[0];
    const last = nodes[nodes.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  // ESC (capture phase to beat WizardShell)
  const handleEsc = React.useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && closeOnEscape) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  React.useEffect(() => {
    if (!isModalOpen) return;
    document.addEventListener("keydown", handleEsc, true);
    document.addEventListener("keydown", handleFocusTrap);
    return () => {
      document.removeEventListener("keydown", handleEsc, true);
      document.removeEventListener("keydown", handleFocusTrap);
    };
  }, [isModalOpen, handleEsc, handleFocusTrap]);

  if (!isModalOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="presentation"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-150"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={contentId}
        tabIndex={-1}
        className={cn(
          "relative z-10 flex w-full flex-col rounded-[var(--v2-radius-md)] border border-[var(--v2-border)] bg-white shadow-2xl animate-in zoom-in-95 fade-in duration-150",
          SIZE_MAP[size],
          "max-h-[90vh]",
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between border-b border-[var(--v2-border)] px-5 py-4">
            <h2
              id={titleId}
              className="text-base font-bold text-[var(--v2-text-strong)]"
            >
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Kapat"
              className="v2-focus-ring -mr-1 flex h-8 w-8 items-center justify-center rounded-full text-[var(--v2-text-muted)] transition hover:bg-slate-100 hover:text-[var(--v2-text-strong)]"
            >
              ✕
            </button>
          </div>
        )}

        {/* Body */}
        <div id={contentId} className="flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-[var(--v2-border)] bg-[var(--v2-surface-muted)] px-5 py-3 rounded-b-[var(--v2-radius-md)]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
